import {isEqual} from 'lodash';
import {DateTime} from 'luxon';
import {useCallback, useMemo, useState} from 'react';

import {CleanHtml, emptyCleanHtml} from '../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {
  AttachmentModel,
  UploadedAttachmentModel,
} from '../../libs/shared/core/src/models/api/attachmentModel';
import {ChannelModel} from '../../libs/shared/core/src/models/api/channelModel';
import {MessageTypesEnum} from '../../libs/shared/core/src/models/api/commonMessageModel';
import {VacationResponderModel} from '../models/api/vacationResponderModel';
import {VacationRespondersFormValidationErrorsEnum} from '../settings/vacationResponders/settingsVacationRespondersFormErrors';

export type OnChannelSwitchChange = (channel: ChannelModel, isChecked: boolean) => void;

export function canEnableVacationResponder(vacationResponder: VacationResponderModel) {
  return vacationResponder.channelIds.length > 0;
}

function validateVacationResponder(vacationResponder: VacationResponderModel): {
  isValid: boolean;
  errors: ReadonlyArray<VacationRespondersFormValidationErrorsEnum>;
} {
  const errors = [];

  if (!vacationResponder.name.trim()) {
    errors.push(VacationRespondersFormValidationErrorsEnum.MISSING_NAME);
  }

  if (vacationResponder.endAt && vacationResponder.endAt < vacationResponder.startAt) {
    errors.push(VacationRespondersFormValidationErrorsEnum.INVALID_TIME_PERIOD);
  }

  if (vacationResponder.isAutoResponderEnabled && vacationResponder.channelIds.length === 0) {
    errors.push(VacationRespondersFormValidationErrorsEnum.MISSING_CHANNEL);
  }

  // We are not checking if the body is empty on purpose since React Native cannot check if an HTML is empty on the go.

  return {isValid: errors.length === 0, errors};
}

interface UseVacationResponderStateArgs {
  namespace: string;
  channels: ReadonlyArray<ChannelModel>;
  initialVacationResponder: VacationResponderModel | undefined;
}

// This is a custom hook that maintains vacation responder state for editing a vacation responder.
export function useVacationResponderEditState(args: UseVacationResponderStateArgs) {
  const {namespace, channels: allChannels, initialVacationResponder} = args;

  const channels = useMemo(
    () => allChannels.filter((channel) => channel.messageType === MessageTypesEnum.EMAIL),
    [allChannels],
  );

  const defaultVacationResponder: VacationResponderModel = useMemo(
    () => createDefaultVacationResponder(namespace, channels),
    [namespace, channels],
  );
  const unchangedVacationResponder: VacationResponderModel = useMemo(
    () => ({...defaultVacationResponder, ...initialVacationResponder}),
    [defaultVacationResponder, initialVacationResponder],
  );
  const [vacationResponderChanges, setVacationResponderChanges] = useState<Partial<VacationResponderModel>>(
    {},
  );
  const vacationResponder = useMemo(
    () => ({...unchangedVacationResponder, ...vacationResponderChanges}),
    [unchangedVacationResponder, vacationResponderChanges],
  );

  const isEditing = initialVacationResponder !== undefined;

  // Track isEmpty instead of body content since it's HTML.
  // When creating, the body is empty.
  const [isBodyEmpty, setIsBodyEmpty] = useState(!isEditing);

  /*
   * Local State update.
   */

  const onVacationResponderChange = useCallback((updated: Partial<VacationResponderModel>) => {
    setVacationResponderChanges((current) => ({...current, ...updated}));
  }, []);

  /*
   * Field callbacks.
   */

  const onNameChange = useCallback(
    (name: string) => {
      onVacationResponderChange({name});
    },
    [onVacationResponderChange],
  );

  const onEnabledChange = useCallback(
    (isChecked: boolean) => {
      // No side effects when disabling.
      if (!isChecked) {
        onVacationResponderChange({isAutoResponderEnabled: false});
        return;
      }

      // If enabling and a channel is selected, simply flip isEnabled.
      if (vacationResponder.channelIds.length > 0) {
        onVacationResponderChange({isAutoResponderEnabled: true});
        return;
      }

      // Otherwise, enable all channels.
      const channelIds = channels.map((c) => c.id);
      onVacationResponderChange({isAutoResponderEnabled: true, channelIds});
    },
    [vacationResponder, channels, onVacationResponderChange],
  );

  const onChannelSwitch = useCallback(
    (channel: ChannelModel, isChecked: boolean) => {
      if (isChecked) {
        const updatedChannelIds = [...vacationResponder.channelIds, channel.id];
        onVacationResponderChange({channelIds: updatedChannelIds});
        return;
      }

      const updatedChannelIds = vacationResponder.channelIds.filter((c) => c !== channel.id);
      if (updatedChannelIds.length === 0) {
        onVacationResponderChange({channelIds: []});
        return;
      }

      onVacationResponderChange({channelIds: updatedChannelIds});
    },
    [vacationResponder.channelIds, onVacationResponderChange],
  );

  const onClearChannelIds = useCallback(() => {
    onVacationResponderChange({channelIds: []});
  }, [onVacationResponderChange]);

  const onSendToContactsChange = useCallback(
    (onlyContacts: boolean) => {
      onVacationResponderChange({onlyContacts});
    },
    [onVacationResponderChange],
  );

  const onStartDateMobileChange = useCallback(
    (date: DateTime) => {
      onVacationResponderChange({startAt: date.toMillis()});
    },
    [onVacationResponderChange],
  );

  const onStartDateWebChange = useCallback(
    (date: string) => {
      const startAt = parseInt(date, 10);
      onVacationResponderChange({startAt});
      if (startAt && vacationResponder.endAt && startAt >= vacationResponder.endAt) {
        onVacationResponderChange({endAt: null});
      }
    },
    [onVacationResponderChange, vacationResponder.endAt],
  );

  const onEndDateMobileChange = useCallback(
    (date: DateTime | null) => {
      onVacationResponderChange({endAt: date ? date.toMillis() : null});
    },
    [onVacationResponderChange],
  );

  const onEndDateWebChange = useCallback(
    (date: string) => {
      const endDate = parseInt(date, 10);
      if (isFinite(endDate)) {
        onVacationResponderChange({endAt: endDate});
      } else {
        onVacationResponderChange({endAt: null});
      }
    },
    [onVacationResponderChange],
  );

  const onBodyChange = useCallback(
    (body: CleanHtml) => {
      onVacationResponderChange({body});
    },
    [onVacationResponderChange],
  );

  const onBodyEmptyChange = useCallback((isEmpty: boolean) => {
    setIsBodyEmpty(isEmpty);
  }, []);

  /*
   * Attachments.
   */

  const onAttachmentUploaded = useCallback(
    (attachment: UploadedAttachmentModel) => {
      const attachments = [...vacationResponder.attachments, attachment];
      onVacationResponderChange({attachments});
    },
    [vacationResponder.attachments, onVacationResponderChange],
  );

  const onRemoveAttachmentPress = useCallback(
    (attachment: AttachmentModel) => {
      const attachments = vacationResponder.attachments.filter((a) => a.cid !== attachment.cid);
      onVacationResponderChange({attachments});
    },
    [vacationResponder.attachments, onVacationResponderChange],
  );

  const onRemoveAttachmentPressWeb = useCallback(
    (cid: string) => {
      const attachments = vacationResponder.attachments.filter((a) => a.cid !== cid);
      onVacationResponderChange({attachments});
    },
    [vacationResponder.attachments, onVacationResponderChange],
  );

  /*
   * Save/Delete actions.
   */

  const {isSaveEnabled, errors} = useMemo(() => {
    if (isEqual(vacationResponder, initialVacationResponder)) {
      return {isSaveEnabled: false, errors: []};
    }

    const {isValid, errors: validationErrors} = validateVacationResponder(vacationResponder);

    // A valid body is required only if the auto-responder is enabled.
    const isBodyValid = !isBodyEmpty || !vacationResponder.isAutoResponderEnabled;

    const bodyValidationErrors: ReadonlyArray<VacationRespondersFormValidationErrorsEnum> = isBodyValid
      ? []
      : [VacationRespondersFormValidationErrorsEnum.MISSING_BODY];

    return {isSaveEnabled: isValid && isBodyValid, errors: [...validationErrors, ...bodyValidationErrors]};
  }, [initialVacationResponder, vacationResponder, isBodyEmpty]);

  return {
    isEditing,
    isSaveEnabled,
    errors,
    vacationResponder,
    onNameChange,
    onEnabledChange,
    onChannelSwitch,
    onClearChannelIds,
    onSendToContactsChange,
    onStartDateMobileChange,
    onEndDateMobileChange,
    onBodyChange,
    onBodyEmptyChange,
    onAttachmentUploaded,
    onRemoveAttachmentPress,
    channels,
    onStartDateWebChange,
    onEndDateWebChange,
    onRemoveAttachmentPressWeb,
  };
}

/*
 * Helpers.
 */

function createDefaultVacationResponder(
  namespace: string,
  channels: ReadonlyArray<ChannelModel>,
): VacationResponderModel {
  return {
    id: -1,
    namespace,
    url: '',
    name: '',
    body: emptyCleanHtml,
    isAutoResponderEnabled: false,
    onlyContacts: false,
    startAt: DateTime.local()
      // Midnight today.
      .set({hour: 0, minute: 0, second: 0, millisecond: 0})
      .toMillis(),
    endAt: null,
    channelIds: channels.map((c) => c.id),
    attachments: [],
    updatedAt: Date.now(),
    lastEditedBy: undefined,
  };
}
